// AboutPage.js — /about: page header, a strip of live site stats, six
// explainer cards in two columns (one on mobile), and a follow-on-X CTA.

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Link,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import InsightsIcon from "@mui/icons-material/Insights";
import GridOnIcon from "@mui/icons-material/GridOn";
import TimelineIcon from "@mui/icons-material/Timeline";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import XIcon from "@mui/icons-material/X";
import PersonIcon from "@mui/icons-material/Person";
import SEO from "./components/SEO";
import { InternalLink, PageHeader } from "./components/Prose";
import { HOME_AWAY, seasonStartYear, summaryCells } from "./components/heatmap/utils";
import { BRAND } from "./theme";
import { cachedScores, fetchScores } from "./api";


function useSiteStats() {
  // A pre-rendered page carries the summary it was built with.
  const [items, setItems] = useState(() => cachedScores("summary=1")?.items || null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchScores("summary=1")
      .then((d) => {
        if (!cancelled) setItems(d?.items || []);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => {
    if (!items) return error ? { error: true } : { loading: true };

    let totalMatches = 0;
    let earliest = null;
    let latest = null;
    for (const s of items) {
      totalMatches += Number(s?.count || 0);
      const f = s?.firstOccurrence?.utcDate;
      if (f) {
        const d = new Date(f);
        if (!earliest || d < earliest) earliest = d;
      }
      const l = s?.latestOccurrence?.utcDate;
      if (l) {
        const d = new Date(l);
        if (!latest || d > latest) latest = d;
      }
    }

    const firstYear = earliest ? seasonStartYear(earliest) : 1992;
    const nowYear = seasonStartYear(latest || new Date());
    const seasons = Math.max(1, nowYear - firstYear + 1);

    return {
      loading: false,
      stats: [
        { value: String(seasons), label: "Seasons" },
        { value: totalMatches.toLocaleString(), label: "Matches" },
        {
          // Matches the heatmap's default (home–away) view.
          value: Object.keys(summaryCells(items, HOME_AWAY)).length.toLocaleString(),
          label: "Home–away results",
        },
        { value: `${firstYear}/${String((firstYear + 1) % 100).padStart(2, "0")}`, label: "First season" },
      ],
    };
  }, [items, error]);
}

function SectionCard({ icon: Icon, eyebrow, title, children }) {
  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 3,
        p: { xs: 2.5, sm: 3.5 },
        background:
          "linear-gradient(180deg, rgba(25,25,32,0.8) 0%, rgba(18,18,24,0.8) 100%)",
        border: `1px solid ${BRAND.divider}`,
        backdropFilter: "blur(14px)",
        boxShadow: "0 6px 30px rgba(0,0,0,0.35)",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            display: "grid",
            placeItems: "center",
            background: BRAND.gradient,
            color: "#fff",
            boxShadow: "0 4px 16px rgba(111,93,245,0.4)",
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box>
          {eyebrow && (
            <Typography
              sx={{
                color: BRAND.cyan,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
              }}
            >
              {eyebrow}
            </Typography>
          )}
          <Typography
            component="h2"
            variant="h6"
            sx={{ color: "#fff", fontWeight: 700, letterSpacing: "-0.01em" }}
          >
            {title}
          </Typography>
        </Box>
      </Stack>
      <Box sx={{ color: BRAND.textSecondary, lineHeight: 1.75, fontSize: 15 }}>
        {children}
      </Box>
    </Box>
  );
}

function Stat({ value, label }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 120,
        textAlign: "center",
        py: 1.25,
        px: 1,
        borderRadius: 2,
        background: "rgba(20,20,26,0.6)",
        border: `1px solid ${BRAND.divider}`,
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: 22, sm: 28 },
          fontWeight: 800,
          letterSpacing: "-0.02em",
          background: BRAND.gradient,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          color: BRAND.textSecondary,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          mt: 0.5,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function AboutPage() {
  const { stats, loading, error } = useSiteStats();

  return (
    <>
      <SEO
        title="About Premier League Scorigami"
        description="Who runs Premier League Scorigami, what it tracks, how the scoreline grid is built from every Premier League result since 1992, and where the data comes from."
        path="/about"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          url: "https://premierleaguescorigami.com/about",
          mainEntity: { "@type": "Person", name: "Mason Maier", url: "https://premierleaguescorigami.com/about" },
        }}
      />
      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 5 } }}>
      <PageHeader
        eyebrow="About"
        title="Every scoreline the Premier League has ever produced"
        lede="Premier League Scorigami is an interactive record of every final score since the league began in August 1992: the common results, the rare ones, and the ones that have never happened."
      />

      {/* Stat strip */}
      {!error && (
        <Stack
          direction="row"
          spacing={{ xs: 1, sm: 2 }}
          useFlexGap
          flexWrap="wrap"
          justifyContent="center"
          sx={{ mb: { xs: 4, sm: 6 } }}
        >
          {loading || !stats
            ? Array.from({ length: 4 }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    flex: 1,
                    minWidth: 120,
                    py: 1.25,
                    px: 1,
                    borderRadius: 2,
                    background: "rgba(20,20,26,0.6)",
                    border: `1px solid ${BRAND.divider}`,
                  }}
                >
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={36}
                    sx={{ mx: "auto", bgcolor: "rgba(255,255,255,0.06)" }}
                  />
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={14}
                    sx={{ mx: "auto", bgcolor: "rgba(255,255,255,0.06)" }}
                  />
                </Box>
              ))
            : stats.map((s) => <Stat key={s.label} {...s} />)}
        </Stack>
      )}

      {/* Content grid: two columns from md up */}
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard icon={InsightsIcon} eyebrow="Origin" title="What is scorigami?">
            <Typography paragraph sx={{ color: "inherit", lineHeight: 1.75 }}>
              Scorigami is the study of final scores: which ones have happened,
              how often, and which never have. The idea was popularized by the
              writer Jon Bois for American football and has since been applied
              across many sports.
            </Typography>
            <Typography sx={{ color: "inherit", lineHeight: 1.75 }}>
              This site applies it to the Premier League, covering every match
              from the opening weekend of the 1992/93 season to the latest
              matchday.
            </Typography>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard icon={GridOnIcon} eyebrow="The grid" title="How to read it">
            <Typography paragraph sx={{ color: "inherit", lineHeight: 1.75 }}>
              In the default <strong style={{ color: "#fff" }}>Home / Away</strong> view,
              the home team's goals run along the bottom and the away team's
              goals run up the side, so a 3–1 home win and a 1–3 away win are
              separate squares. Each square shows how many times that result has
              happened, and its color shows how common it is. Empty squares are
              results that have never happened.
            </Typography>
            <Typography sx={{ color: "inherit", lineHeight: 1.75 }}>
              The <strong style={{ color: "#fff" }}>Winner / Loser</strong> view
              ignores venue: goals scored run along the bottom and goals conceded
              up the side, so each result appears in two mirrored squares.
            </Typography>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard icon={TimelineIcon} eyebrow="Seasons" title="All-time and single seasons">
            <Typography paragraph sx={{ color: "inherit", lineHeight: 1.75 }}>
              The season picker switches between the complete history and any
              individual season. In a single season, the color scale adjusts to
              that season's much smaller numbers.
            </Typography>
            <Typography sx={{ color: "inherit", lineHeight: 1.75 }}>
              The side panel then shows how many matches and different results
              the season produced, and how many of its results were new to
              Premier League history.
            </Typography>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard icon={TouchAppIcon} eyebrow="Explore" title="Looking up a result">
            <Typography paragraph sx={{ color: "inherit", lineHeight: 1.75 }}>
              Hover over a square for a quick summary, or select it for the full
              history: the first and most recent matches, a season-by-season
              chart, and every match when you are viewing a single season. Any
              result can be shared to X or downloaded as an image.
            </Typography>
            <Typography sx={{ color: "inherit", lineHeight: 1.75 }}>
              The <InternalLink to="/records">Records</InternalLink> page
              collects the rarest results, biggest wins and highest-scoring
              matches, and the <InternalLink to="/articles">Articles</InternalLink>{" "}
              section explains the ideas behind the site.
            </Typography>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard icon={CloudSyncIcon} eyebrow="Data" title="Sources and updates">
            <Typography paragraph sx={{ color: "inherit", lineHeight: 1.75 }}>
              Results come from football-data.org and are added automatically,
              usually within 15 minutes of the final whistle. If a score is
              corrected after full time, the stored result is updated too.
            </Typography>
            <Typography sx={{ color: "inherit", lineHeight: 1.75 }}>
              The complete history has been checked, match by match, against
              two independent archives: football-data.co.uk and the
              openfootball project.
            </Typography>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard icon={XIcon} eyebrow="On X" title="Results as they happen">
            <Typography paragraph sx={{ color: "inherit", lineHeight: 1.75 }}>
              The{" "}
              <Link
                href="https://x.com/pl_gami"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: BRAND.cyan, fontWeight: 600 }}
              >
                @pl_gami
              </Link>{" "}
              account posts every Premier League result as matches finish, with
              extra detail when a result is rare or has never happened before.
            </Typography>
            <Typography sx={{ color: "inherit", lineHeight: 1.75 }}>
              A round-up of each day's results follows the last match.
            </Typography>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <SectionCard icon={PersonIcon} eyebrow="Who runs it" title="Mason Maier">
            <Typography sx={{ color: "inherit", lineHeight: 1.75 }}>
              Premier League Scorigami is an independent project built and run by Mason Maier: the data
              pipeline, the checks against independent archives, this site and the @pl_gami account. It is not
              affiliated with the Premier League or any club. Corrections, ideas and press enquiries are welcome
              through the <InternalLink to="/contact">Contact</InternalLink> page.
            </Typography>
          </SectionCard>
        </Grid>
      </Grid>

      {/* CTA */}
      <Box
        sx={{
          mt: { xs: 4, sm: 6 },
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          textAlign: "center",
          background:
            "linear-gradient(135deg, rgba(57,208,255,0.08), rgba(111,93,245,0.12))",
          border: `1px solid ${BRAND.divider}`,
        }}
      >
        <Typography
          component="h2"
          variant="h5"
          sx={{ color: "#fff", fontWeight: 700, mb: 1 }}
        >
          Follow new results as they happen
        </Typography>
        <Typography
          sx={{
            color: BRAND.textSecondary,
            maxWidth: 560,
            mx: "auto",
            mb: 2.5,
          }}
        >
          Every Premier League result is posted within minutes of full time,
          with each new scorigami highlighted.
        </Typography>
        <Link
          href="https://x.com/pl_gami"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2.5,
            py: 1.25,
            borderRadius: 999,
            fontWeight: 700,
            color: "#fff",
            textDecoration: "none",
            background: BRAND.gradient,
            boxShadow: "0 6px 20px rgba(111,93,245,0.4)",
          }}
        >
          <XIcon fontSize="small" /> Follow @pl_gami
        </Link>
      </Box>
      </Container>
    </>
  );
}
